import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, forwardRef, useImperativeHandle, } from 'react';
import Editor from '@monaco-editor/react';
import { Button } from '../Button';
import ArrowThinLeft from '../Icon/ArrowThinLeft';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle, } from './Dialog';
const HtmlEditor = forwardRef(({ initialValue = '', onChange, onSave }, ref) => {
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState(initialValue);
    const [originalCode, setOriginalCode] = useState(initialValue);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [copyStatus, setCopyStatus] = useState('Copy Code');
    const [saveStatus, setSaveStatus] = useState('Save Code');
    const editorRef = useRef(null);
    const containerRef = useRef(null); // Reference for the container holding the editor
    useImperativeHandle(ref, () => ({
        handleOpen: () => setOpen(true),
    }));
    const handleOpen = () => {
        setOriginalCode(code);
        setOpen(true);
    };
    const handleClose = () => {
        if (hasUnsavedChanges) {
            const confirmClose = window.confirm('You have unsaved changes. If you go back now, all changes will be lost. Are you sure you want to continue?');
            if (!confirmClose)
                return;
        }
        setOpen(false);
        setHasUnsavedChanges(false);
        setCode(originalCode);
    };
    const handleSave = () => {
        setOriginalCode(code);
        setHasUnsavedChanges(false);
        if (onSave)
            onSave(code);
        setSaveStatus('Saved!');
        setTimeout(() => setSaveStatus('Save Code'), 1000);
    };
    const handleCopyCode = () => {
        navigator.clipboard.writeText(code);
        setCopyStatus('Copied!');
        setTimeout(() => setCopyStatus('Copy Code'), 1000);
    };
    const handleEditorChange = (value) => {
        if (value !== undefined) {
            setCode(value);
            setHasUnsavedChanges(value !== originalCode);
            if (onChange)
                onChange(value);
        }
    };
    const handleEditorDidMount = (editor) => {
        editorRef.current = editor;
        handleResize();
        window.addEventListener('resize', handleResize);
    };
    const handleResize = () => {
        if (editorRef.current && containerRef.current) {
            const newWidth = window.innerWidth * 0.9;
            containerRef.current.style.width = `${newWidth - 50}px`;
            editorRef.current.layout();
        }
    };
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('resize', handleResize);
        };
    }, [hasUnsavedChanges]);
    useEffect(() => {
        setCode(initialValue);
        setOriginalCode(initialValue);
    }, [initialValue]);
    return (_jsxs(AlertDialog, { open: open, children: [_jsx("button", { onClick: handleOpen, className: "text-slate-700", children: "Edit" }), _jsxs(AlertDialogContent, { className: "w-[90%]", children: [_jsx(AlertDialogHeader, { children: _jsxs("div", { className: "flex justify-between", children: [_jsxs("div", { className: "flex items-center space-x-3", children: [_jsxs("button", { onClick: handleClose, className: "flex text-sm items-center text-gray-600 space-x-1", children: [_jsx(ArrowThinLeft, { width: "24", height: "24" }), _jsx("span", { children: "Go Back" })] }), _jsx(AlertDialogTitle, { className: "text-lg font-bold", children: "Email Template HTML" })] }), _jsxs("div", { className: "flex items-center space-x-3", children: [hasUnsavedChanges && (_jsx("div", { className: "text-sm font-normal text-gray-400", children: "This document has not been saved" })), _jsx(Button, { className: "w-[120px]", variant: "outline", onClick: handleCopyCode, children: copyStatus }), _jsx(Button, { className: "w-[120px]", onClick: handleSave, children: saveStatus })] })] }) }), _jsx(AlertDialogDescription, { className: "flex w-full h-full", children: _jsx("div", { ref: containerRef, className: "h-[calc(100vh-200px)] w-full", children: _jsx(Editor, { height: "100%", width: "100%", defaultLanguage: "html", value: code, onChange: handleEditorChange, onMount: handleEditorDidMount, theme: "vs-dark", options: {
                                    selectOnLineNumbers: false,
                                    lineNumbers: 'on',
                                    automaticLayout: false,
                                    scrollBeyondLastLine: false,
                                } }) }) })] })] }));
});
export default HtmlEditor;
